import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Layout } from './components/Layout';
import { LoadingBlock } from './components/ui';
import { LoginPage } from './pages/Login';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordReset';
import { NotFoundPage } from './pages/NotFound';
import { OverviewPage } from './pages/Overview';
import { EmployeesPage } from './pages/Employees';
import { EmployeeDetailPage } from './pages/EmployeeDetail';
import { PointsPage } from './pages/Points';
import { WorkPage } from './pages/Work';
import { EvidenceItemPage } from './pages/EvidenceItem';
import { ProjectsPage } from './pages/Projects';
import { ProjectDetailPage } from './pages/ProjectDetail';
import { ClientDetailPage } from './pages/ClientDetail';
import { FinancePage } from './pages/Finance';
import { InsightsPage } from './pages/Insights';
import { ReportsPage } from './pages/Reports';
import { ReportDetailPage } from './pages/ReportDetail';
import { AccessPage } from './pages/Access';
import { SettingsPage } from './pages/Settings';

/**
 * The Executive Dashboard's pages (CR-008 §3.1). Signed out, only sign-in and the password reset answer;
 * a session that ends — access revoked, the account switched off — lands back on sign-in, saying so.
 */
export function App() {
  const { user, loading } = useAuth();
  const [ended, setEnded] = useState(false);
  useEffect(() => {
    const onEnded = () => setEnded(true);
    window.addEventListener('viganium:signed-out', onEnded);
    return () => window.removeEventListener('viganium:signed-out', onEnded);
  }, []);

  if (loading) return <LoadingBlock />;

  if (!user) {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<LoginPage ended={ended} />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<OverviewPage />} />
        <Route path="/employees" element={<EmployeesPage />} />
        <Route path="/employees/:userId" element={<EmployeeDetailPage />} />
        <Route path="/points" element={<PointsPage />} />
        <Route path="/work" element={<WorkPage />} />
        <Route path="/work/evidence/:type/:id" element={<EvidenceItemPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/clients/:id" element={<ClientDetailPage />} />
        <Route path="/finance" element={<FinancePage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/:id" element={<ReportDetailPage />} />
        <Route path="/access" element={<AccessPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* Signed in, the sign-in and reset links lead home. */}
        <Route path="/forgot-password" element={<Navigate to="/" replace />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  );
}
